import { useQuery } from '@tanstack/react-query'

import { graphqlClient } from '../client'
import type { ListDeviceTypesQuery } from '../codegen/graphql'
import { ListDeviceTypesDocument } from '../codegen/graphql'

export type { DeviceTypeCategory } from '../codegen/graphql'

export type DeviceTypeEntry = ListDeviceTypesQuery['deviceTypes'][number]

// Backwards-compatible aliases — all three types resolve to the same
// underlying DeviceTypeEntry since the queries were consolidated.
export type DeviceTypeCatalogEntry = DeviceTypeEntry
export type DeviceTypeDriverEntry = DeviceTypeEntry

// ---------------------------------------------------------------------------
// Single source of truth — TanStack Query deduplicates the request.
// ---------------------------------------------------------------------------

const DEVICE_TYPES_QUERY_KEY = ['deviceTypes'] as const
const STALE_TIME = 60_000 // 60 s — matches BFF DataLoader TTL

async function fetchDeviceTypes() {
  const response = await graphqlClient.request<ListDeviceTypesQuery>(
    ListDeviceTypesDocument,
  )
  return response.deviceTypes
}

type UseDeviceTypesOptions = {
  enabled?: boolean
}

/**
 * Full device type catalog — all fields including geometry, slots, tags.
 * Used by DevicePropertiesPanel and RegisterDeviceDialog.
 */
export function useDeviceTypes(options?: UseDeviceTypesOptions) {
  return useQuery({
    queryKey: DEVICE_TYPES_QUERY_KEY,
    queryFn: fetchDeviceTypes,
    staleTime: STALE_TIME,
    enabled: options?.enabled,
  })
}

/**
 * Lightweight projection for registration UI.
 * Same underlying request as useDeviceTypes — TanStack Query deduplicates.
 */
export function useDeviceTypeCatalog() {
  return useQuery({
    queryKey: DEVICE_TYPES_QUERY_KEY,
    queryFn: fetchDeviceTypes,
    staleTime: STALE_TIME,
  })
}

/**
 * Driver info projection for workflow canvas validation.
 * Same underlying request as useDeviceTypes — TanStack Query deduplicates.
 */
export function useDeviceTypeDriverInfo() {
  return useQuery({
    queryKey: DEVICE_TYPES_QUERY_KEY,
    queryFn: fetchDeviceTypes,
    staleTime: STALE_TIME,
  })
}
